/**
 * Cross-platform geolocation helper.
 *
 * On the web this delegates to `navigator.geolocation.getCurrentPosition`.
 * On Capacitor native (iOS / Android) the browser API often *silently hangs*
 * because the WebView never receives a permission prompt from the OS. We must
 * ask the OS explicitly via `@capacitor/geolocation`, which triggers the
 * native "Allow Linkola to use your location?" dialog. Once granted, the
 * plugin returns coordinates in the exact same shape as the browser API.
 *
 * Signature intentionally mirrors `navigator.geolocation.getCurrentPosition`
 * so call-sites only change the function name — success callback receives a
 * `{ coords: { latitude, longitude, accuracy } }` object, error callback
 * receives an object with a `.code` (1 = PERMISSION_DENIED, matching the W3C
 * PositionError contract used across the codebase).
 */

let capModulePromise = null;

const isNative = () => {
  try {
    // Capacitor 6 exposes a global on `window` when running inside a
    // WKWebView / Android WebView shell. Anything else (Safari, Chrome,
    // Emergent preview) reports "web".
    return typeof window !== "undefined"
      && window.Capacitor
      && typeof window.Capacitor.isNativePlatform === "function"
      && window.Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// Deliberately returns the whole module namespace, NOT `m.Geolocation`
// directly. Capacitor plugin objects are Proxies whose `get` trap returns a
// callable RPC wrapper for ANY property name — including "then", which
// isn't a real plugin method. If the plugin object itself were returned
// from inside this `.then()` (or an async function), the JS promise
// resolution algorithm sees a truthy `.then` on it, decides it's a nested
// thenable, and "assimilates" it by calling `plugin.then(resolve, reject)`.
// Capacitor treats that as an RPC call to a method named "then", which
// doesn't exist, and throws an unhandled rejection ('"Geolocation.then()"
// is not implemented on ios/android') while the outer await never settles
// — the exact "spins forever, then crashes" bug this file exists to avoid.
// Extracting `.Geolocation` as a plain property read AFTER awaiting sidesteps
// promise-resolution entirely, since it's not a value being resolved.
const loadCapPlugin = () => {
  if (!capModulePromise) {
    capModulePromise = import("@capacitor/geolocation");
  }
  return capModulePromise;
};

const makeError = (code, message) => ({ code, message });

/**
 * @param {(pos: {coords:{latitude:number,longitude:number,accuracy?:number}}) => void} onSuccess
 * @param {(err: {code:number,message:string}) => void} [onError]
 * @param {{enableHighAccuracy?:boolean,timeout?:number,maximumAge?:number}} [options]
 */
export const getCurrentPositionSafe = (onSuccess, onError, options = {}) => {
  const opts = {
    enableHighAccuracy: options.enableHighAccuracy ?? false,
    timeout: options.timeout ?? 10000,
    maximumAge: options.maximumAge ?? 60000,
  };

  if (isNative()) {
    (async () => {
      try {
        const capModule = await loadCapPlugin();
        const Geolocation = capModule.Geolocation;
        // Explicitly request permission — on iOS this shows the system
        // "Allow While Using App" prompt, on Android it shows the runtime
        // ACCESS_FINE_LOCATION dialog. Without this the fetch below hangs.
        let perm;
        try { perm = await Geolocation.checkPermissions(); }
        catch { perm = null; }
        if (!perm || (perm.location !== "granted" && perm.coarseLocation !== "granted")) {
          const req = await Geolocation.requestPermissions({ permissions: ["location"] });
          if (req.location !== "granted" && req.coarseLocation !== "granted") {
            if (onError) onError(makeError(1, "Location permission denied"));
            return;
          }
        }
        const pos = await Geolocation.getCurrentPosition(opts);
        onSuccess({
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
        });
      } catch (err) {
        // Capacitor throws generic errors; try to detect a denial.
        const msg = String(err?.message || err || "").toLowerCase();
        const denied = msg.includes("denied") || msg.includes("permission");
        if (onError) onError(makeError(denied ? 1 : 2, err?.message || "Location unavailable"));
      }
    })();
    return;
  }

  // Browser path.
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    if (onError) onError(makeError(2, "Geolocation not supported"));
    return;
  }
  // Some browser/OS combos (Chrome + macOS with Location Services off for
  // the browser) never invoke EITHER callback when the OS-level permission
  // broker stalls — `PositionOptions.timeout` is silently ignored in that
  // case. Enforce our own timeout so callers' loading state can never hang
  // forever.
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    if (onError) onError(makeError(3, "Location request timed out"));
  }, opts.timeout);
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      onSuccess(pos);
    },
    (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (onError) onError(err);
    },
    opts,
  );
};

/**
 * Quick capability check that works on both web and native.
 */
export const isGeolocationSupported = () => {
  if (isNative()) return true;
  return typeof navigator !== "undefined" && !!navigator.geolocation;
};
